import React, { FC, Fragment, MouseEvent, useState } from 'react';
import {
  Checkbox,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { makeStyles } from '@material-ui/core/styles';
import { useFormikContext } from 'formik';
import { find } from 'lodash';

import { ClientDisclosure, ContentDisclosures, ContentInput } from 'api';
import { useClient } from 'hooks';

interface IProps {
  disclosureItem: ContentDisclosures;
  index: number;
}

const useStyles = makeStyles(theme => ({
  icon: {
    backgroundColor: theme.palette.grey[200],
    marginLeft: theme.spacing(1.5),
    '&:hover': {
      backgroundColor: theme.palette.grey[300],
    },
  },
  list: {
    paddingTop: theme.spacing(),
    paddingBottom: theme.spacing(),
  },
  menuTitle: {
    padding: theme.spacing(1, 0, 2),
    '&:focus': {
      outline: 0,
    },
  },
  menuItem: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  paper: {
    width: 300,
  },
  listText: {
    flex: 1,
  },
  listIcon: {
    marginRight: theme.spacing(0),
  },
}));

const DisclosureIcon: FC<IProps> = ({ disclosureItem, index }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { setFieldValue, values } = useFormikContext<ContentInput>();
  const [anchorEl, setAnchorEl] = useState(null);
  const { disclosures } = useClient();

  function findDisclosure(id: ClientDisclosure['id']) {
    return find(values.disclosures, { network: disclosureItem.network, disclosure_id: id });
  }

  function handleOnClickIcon(event: MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleToggle(item: ClientDisclosure) {
    const shouldToggleOff = item.id === values.disclosures[index].disclosure_id;

    if (shouldToggleOff) {
      setFieldValue(`disclosures[${index}]`, { ...values.disclosures[index], disclosure_id: '' });
    } else {
      setFieldValue(`disclosures[${index}].network`, disclosureItem.network);
      setFieldValue(`disclosures[${index}].disclosure_id`, item.id);
    }

    handleClose();
  }

  function renderMenuItems(item: ClientDisclosure) {
    const { id, disclosure } = item;

    return (
      <MenuItem dense key={id} onClick={() => handleToggle(item)} className={classes.menuItem}>
        <Typography title={disclosure} noWrap className={classes.listText}>
          {disclosure}
        </Typography>
        <ListItemIcon className={classes.listIcon}>
          <Checkbox color="primary" checked={!!findDisclosure(id)} />
        </ListItemIcon>
      </MenuItem>
    );
  }

  return (
    <Fragment>
      <IconButton onClick={handleOnClickIcon} className={classes.icon} size="small">
        <Tooltip title={t('admin:disclosures')} placement="top">
          <MoreHorizIcon />
        </Tooltip>
      </IconButton>
      <Menu
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        classes={{ paper: classes.paper, list: classes.list }}>
        <Typography align="center" variant="body2" className={classes.menuTitle}>
          {t('admin:disclosures')}
        </Typography>
        {disclosures.map(renderMenuItems)}
      </Menu>
    </Fragment>
  );
};

export default DisclosureIcon;
